import { $ariaBool } from './chunk-EVP5E6AO.js';
import { onPress, setAttributeIfEmpty, isElementParent } from './chunk-Z6A4GYXE.js';
import { menuContext } from './chunk-HSL7QSWM.js';
import { __decorateClass } from './chunk-VPSTUDLO.js';
import { signal, hasProvidedContext, useContext, provideContext, effect, peek, tick, onDispose } from 'maverick.js';
import { defineElement, method, Component } from 'maverick.js/element';
import { setAttribute, listenEvent, ariaBool, isKeyboardEvent, setStyle } from 'maverick.js/std';

var FOCUSABLE_ELEMENTS_SELECTOR = /* @__PURE__ */ [
  "a[href]",
  "[tabindex]",
  "input",
  "select",
  "button"
].map((selector) => `${selector}:not([aria-hidden])`).join(",");
var VALID_KEYS = /* @__PURE__ */ new Set([
  "Escape",
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "PageUp",
  "End",
  "PageDown",
  "Enter",
  " "
]);
var MenuFocusController = class {
  constructor(_closeMenu) {
    this.Wg = _closeMenu;
    this.W = 0;
    this.oa = null;
    this.Q = [];
  }
  get p() {
    return this.Q;
  }
  Tg(el) {
    listenEvent(el, "focus", this.Na.bind(this));
    this.oa = el;
  }
  Ug() {
    if (!this.oa)
      return;
    this.J();
    listenEvent(this.oa, "keyup", this.Ua.bind(this));
    listenEvent(this.oa, "keydown", this.Va.bind(this));
    onDispose(() => {
      this.W = 0;
      this.Q = [];
    });
  }
  Ia(index) {
    this.W = index;
    this.Q[index]?.focus();
    this.Q[index]?.scrollIntoView({
      block: "center"
    });
  }
  Na() {
    const index = this.Q.findIndex((el) => el.getAttribute("aria-checked") === "true");
    this.Ia(index >= 0 ? index : 0);
  }
  J() {
    this.W = 0;
    this.Q = this.Vg();
  }
  Ua(event) {
    if (!VALID_KEYS.has(event.key))
      return;
    event.stopPropagation();
    event.preventDefault();
  }
  Va(event) {
    if (!VALID_KEYS.has(event.key))
      return;
    event.stopPropagation();
    event.preventDefault();
    switch (event.key) {
      case "Escape":
        this.Wg(event);
        break;
      case "Tab":
        this.Ia(this.Ic(event.shiftKey ? -1 : 1));
        break;
      case "ArrowUp":
        this.Ia(this.Ic(-1));
        break;
      case "ArrowDown":
        this.Ia(this.Ic(1));
        break;
      case "Home":
      case "PageUp":
        this.Ia(0);
        break;
      case "End":
      case "PageDown":
        this.Ia(this.Q.length - 1);
        break;
    }
  }
  Ic(delta) {
    let index = this.W;
    do {
      index = (index + delta + this.Q.length) % this.Q.length;
    } while (this.Q[index].offsetParent === null);
    return index;
  }
  Vg() {
    if (!this.oa)
      return [];
    const focusableElements = this.oa.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR), elements = [];
    const is = (node) => node.hasAttribute("data-media-menu-items");
    for (const el of focusableElements) {
      if (isElementParent(this.oa, el, is))
        elements.push(el);
    }
    return elements;
  }
};

// src/player/ui/menu/menu.tsx
var idCount = 0;
var Menu = class extends Component {
  constructor(instance) {
    super(instance);
    this.X = signal(false);
    this.Ud = signal(false);
    this.Vd = signal(false);
    this.xb = /* @__PURE__ */ new Set();
    this.Lc = null;
    this.I = null;
    this.Kh = this.pa.bind(this);
    this.dh = this.eh.bind(this);
    this.bh = this.fh.bind(this);
    this.ch = this.gh.bind(this);
    const currentIdCount = ++idCount;
    this.Jc = `media-menu-${currentIdCount}`;
    this.Kc = `media-menu-button-${currentIdCount}`;
    if (hasProvidedContext(menuContext)) {
      this.Ja = useContext(menuContext);
    }
    this.Mc = new MenuFocusController(this.close.bind(this));
    provideContext(menuContext, {
      X: this.X,
      ab: signal(""),
      pa: this.pa.bind(this),
      Nc: this.Nc.bind(this),
      Oc: this.Oc.bind(this),
      Pc: this.Pc.bind(this),
      Qc: this.Qc.bind(this)
    });
  }
  onAttach(el) {
    if (this.Ja)
      setAttribute(el, "data-submenu", "");
    const { position } = this.$props;
    this.setAttributes({ position });
  }
  onConnect(el) {
    effect(this.Xg.bind(this));
    this.Ja?.Qc(el);
  }
  onDestroy() {
    this.Lc = null;
    this.I = null;
  }
  Xg() {
    const expanded = this.Wd();
    this.Ga();
    this.Xd(expanded);
    if (!expanded)
      return;
    this.Mc.Ug();
    const closeTarget = this.Yg();
    if (closeTarget) {
      onPress(closeTarget, this.Zg.bind(this));
    }
    this.listen("pointerup", this._g.bind(this));
    listenEvent(window, "pointerup", this.$g.bind(this));
  }
  Nc(el) {
    const isMenuItem = !!this.Ja, isExpanded = this.Wd.bind(this), isARIAExpanded = $ariaBool(isExpanded), isARIADisabled = $ariaBool(this.q.bind(this));
    setAttributeIfEmpty(el, "tabindex", isMenuItem ? "-1" : "0");
    setAttributeIfEmpty(el, "role", isMenuItem ? "menuitem" : "button");
    setAttribute(el, "id", this.Kc);
    setAttribute(el, "aria-controls", this.Jc);
    setAttribute(el, "aria-haspopup", "true");
    effect(() => {
      setAttribute(el, "aria-disabled", isARIADisabled());
      setAttribute(el, "aria-expanded", isARIAExpanded());
      if (!isMenuItem)
        setAttribute(el, "aria-pressed", isARIAExpanded());
      setAttribute(el, "data-pressed", isExpanded());
    });
    setAttribute(el, "data-media-button", !isMenuItem);
    setAttribute(el, "data-media-menu-button", "");
    onPress(el, this.ah.bind(this));
    this.Lc = el;
  }
  Oc(el) {
    setAttributeIfEmpty(el, "role", "menu");
    setAttributeIfEmpty(el, "tabindex", "-1");
    setAttribute(el, "id", this.Jc);
    setAttribute(el, "aria-describedby", this.Kc);
    setAttribute(el, "data-media-menu-items", "");
    this.I = el;
    this.Mc.Tg(el);
    this.Xd(peek(this.X));
  }
  Xd(expanded) {
    if (this.I)
      setAttribute(this.I, "aria-hidden", ariaBool(!expanded));
  }
  Pc(disabled) {
    this.Vd.set(disabled);
  }
  ah(event) {
    if (this.Ja)
      event.stopPropagation();
    if (this.q())
      return;
    this.X.set((expanded) => !expanded);
    tick();
    if (isKeyboardEvent(event))
      this.I?.focus();
    this.o(event);
  }
  o(trigger) {
    this.dispatch(peek(this.X) ? "open" : "close", { trigger });
  }
  Wd() {
    return !this.q() && this.X();
  }
  q() {
    return this.Ud() || this.Vd();
  }
  pa(disabled) {
    this.Ud.set(disabled);
  }
  _g(event) {
    event.stopPropagation();
  }
  $g() {
    if (this.Ja)
      return setTimeout(this.close.bind(this), 300);
    this.close();
  }
  Zg(event) {
    event.stopPropagation();
    this.close(event);
  }
  Yg() {
    const target = this.el.querySelector('[slot="close-target"]');
    return isElementParent(this.el, target) ? target : null;
  }
  Qc(el) {
    this.xb.add(el);
    listenEvent(el, "open", this.bh);
    listenEvent(el, "close", this.ch);
    onDispose(this.dh);
  }
  eh(el) {
    this.xb.delete(el);
  }
  fh(event) {
    for (const el of this.xb) {
      if (el !== event.target)
        el.setAttribute("aria-hidden", "true");
    }
    this.Ga();
  }
  gh() {
    for (const el of this.xb)
      el.removeAttribute("aria-hidden");
    this.Ga();
  }
  Ga() {
    if (!this.I)
      return;
    let style = getComputedStyle(this.I), height = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    let children = [...this.I.children];
    if (children[0]?.localName === "shadow-root") {
      children.push(...children[0].children);
    }
    for (const child of children) {
      height += child.offsetHeight;
    }
    requestAnimationFrame(() => {
      setAttribute(this.I, "data-resizing", "");
      setTimeout(() => {
        setAttribute(this.I, "data-resizing", false);
      }, 250);
      setStyle(this.I, "--menu-height", height + "px");
    });
  }
  open(trigger) {
    this.X.set(true);
    tick();
    this.o(trigger);
    if (isKeyboardEvent(trigger))
      this.I?.focus();
  }
  close(trigger) {
    this.X.set(false);
    tick();
    if (isKeyboardEvent(trigger)) {
      requestAnimationFrame(() => {
        this.Lc?.focus();
      });
    }
    this.o(trigger);
  }
};
Menu.el = defineElement({
  tagName: "media-menu",
  props: { position: null }
});
__decorateClass([
  method
], Menu.prototype, "open", 1);
__decorateClass([
  method
], Menu.prototype, "close", 1);

export { Menu };
